import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, BookOpen, Mail, ArrowLeft } from 'lucide-react';

const SupportPage: React.FC = () => {
  const { t } = useTranslation("common");
  const faqs = [
    { question: t("support.faq.q1"), answer: t("support.faq.a1") },
    { question: t("support.faq.q2"), answer: t("support.faq.a2") },
    { question: t("support.faq.q3"), answer: t("support.faq.a3") },
    { question: t("support.faq.q4"), answer: t("support.faq.a4") },
    { question: t("support.faq.q5"), answer: t("support.faq.a5") },
    { question: t("support.faq.q6"), answer: t("support.faq.a6") },
  ];

  const userGuides = [
    {
      title: t("support.guides.guide1_title"),
      description: t("support.guides.guide1_desc"),
      icon: BookOpen,
    },
    {
      title: t("support.guides.guide2_title"),
      description: t("support.guides.guide2_desc"),
      icon: HelpCircle,
    },
    {
      title: t("support.guides.guide3_title"),
      description: t("support.guides.guide3_desc"),
      icon: BookOpen,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4">
      <div className="w-full max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col items-center space-y-3 mb-8 pt-8">
          <img src="/tool.png" alt="Moubarati Logo" className="h-16 w-16" />
          <h1 className="text-4xl font-bold text-white">
            Moubarati - مباراتي
          </h1>
          <p className="text-gray-400 text-lg">{t("support.page_title")}</p>
        </div>

        {/* Contact Info Card */}
        <div className="bg-gradient-to-br from-brand-primary/20 to-brand-secondary/10 border border-brand-primary/30 rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-start space-x-4 rtl:space-x-reverse">
            <Mail className="h-8 w-8 text-brand-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white mb-2">
                {t("support.contact_title")}
              </h2>
              <p className="text-gray-300 mb-3">
                {t("support.contact_intro")}
              </p>
              <a
                href="mailto:lirf@moubarati.com"
                className="inline-flex items-center text-brand-primary hover:text-brand-secondary font-semibold transition-colors"
              >
                lirf@moubarati.com
              </a>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-brand-primary mb-6 flex items-center">
            <HelpCircle className="me-3 h-6 w-6" />
            {t("support.faq_title")}
          </h2>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-900/50 rounded-lg p-5 border border-gray-700/50 hover:border-brand-primary/30 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* User Guides Section */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-brand-primary mb-6 flex items-center">
            <BookOpen className="me-3 h-6 w-6" />
            {t("support.guides_title")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {userGuides.map((guide, index) => (
              <div
                key={index}
                className="bg-gray-900/50 rounded-lg p-5 border border-gray-700/50 hover:border-brand-primary/50 transition-all hover:shadow-lg hover:shadow-brand-primary/20 cursor-pointer group"
              >
                <guide.icon className="h-10 w-10 text-brand-primary mb-3 group-hover:scale-110 transition-transform" />

                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-brand-primary transition-colors">
                  {guide.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {guide.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-brand-primary/10 border border-brand-primary/30 rounded-lg">
            <p className="text-gray-300 text-center">
              <span className="font-semibold text-white">{t("support.guides_note_label")}</span> {t("support.guides_note_text")}
            </p>
          </div>
        </div>

        {/* Additional Help Section */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-brand-primary mb-4">
            {t("support.extra_help_title")}
          </h2>
          <div className="space-y-3">
            <p className="text-gray-300 leading-relaxed">
              {t("support.extra_help_text")}
            </p>
          </div>
        </div>

        {/* Back to Login Button */}
        <div className="text-center pb-8">
          <a
            href="/"
            className="inline-flex items-center bg-brand-primary hover:bg-brand-secondary text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl"
          >
            <ArrowLeft className="me-2 h-5 w-5 rtl:rotate-180" />
            {t("support.back_home_btn")}
          </a>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm pb-4">
          <p>{t("support.footer_copyright")}</p>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
